import React, { createContext, useContext, useState, useCallback } from 'react';
import { hashnodeFetch } from '../services/blog-api';
import { getAllPosts, getSinglePost } from '../services/blog-queries';

const BlogContext = createContext();

export const useBlog = () => {
  const context = useContext(BlogContext);
  if (!context) {
    throw new Error('useBlog must be used within a BlogProvider');
  }
  return context;
};

export const BlogProvider = ({ children }) => {
  const [posts, setPosts] = useState([]);
  const [currentPost, setCurrentPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageInfo, setPageInfo] = useState(null);

  const fetchPosts = useCallback(async (after = null) => {
    setLoading(true);
    setError(null);
    
    // Check if Hashnode is configured
    if (!process.env.REACT_APP_HASHNODE_HOST_NAME) {
      setError('Blog system not configured. Please set up Hashnode environment variables.');
      setLoading(false);
      return;
    }
    
    try {
      const response = await hashnodeFetch(getAllPosts, { after });
      const data = await response.json();
      
      if (data.errors) {
        throw new Error(data.errors[0].message);
      }
      
      const publication = data.data.publication;
      
      if (!publication || !publication.posts) {
        throw new Error('No publication or posts found');
      }
      
      const newPosts = publication.posts.edges.map(edge => edge.node);
      
      if (after) {
        setPosts(prevPosts => [...prevPosts, ...newPosts]);
      } else {
        setPosts(newPosts);
      }
      
      setPageInfo(publication.posts.pageInfo);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching posts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPost = useCallback(async (slug) => {
    setLoading(true);
    setError(null);
    
    // Check if Hashnode is configured
    if (!process.env.REACT_APP_HASHNODE_HOST_NAME) {
      setError('Blog system not configured. Please set up Hashnode environment variables.');
      setLoading(false);
      return;
    }
    
    try {
      const response = await hashnodeFetch(getSinglePost, { slug });
      const data = await response.json();
      
      if (data.errors) {
        throw new Error(data.errors[0].message);
      }
      
      const post = data.data.publication.post;
      
      if (!post) {
        throw new Error('Post not found');
      }
      
      setCurrentPost(post);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching post:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearCurrentPost = useCallback(() => {
    setCurrentPost(null);
  }, []);

  const value = {
    posts,
    currentPost,
    loading,
    error,
    pageInfo,
    fetchPosts,
    fetchPost,
    clearCurrentPost,
  };

  return (
    <BlogContext.Provider value={value}>
      {children}
    </BlogContext.Provider>
  );
};